import { ReactNode, useMemo } from 'react';

import { observer } from 'mobx-react-lite';
import { useRouter } from 'next/router';

import { BannerWrapper, Disclosure } from '@r-client/republic/feature/banners';
import { Footer, IFooterProps } from '@r-client/republic/feature/layout';
import { Layout, TLayoutConfig } from '@r-client/republic/feature/layout';
import { LevelupMainHeader } from '@r-client/republic/feature/levelup';
import {
  HEADER_THEMES,
  IRegionSwitcherSettings,
  MainHeader,
} from '@r-client/republic/feature/main-header';
import { ScrollTopButton } from '@r-client/republic/ui/core';
import { useAnalytics } from '@r-client/shared/data/analytics';
import { useAuth } from '@r-client/shared/data/auth';
import { AdminMenu } from '@r-client/shared/feature/admin-menu';
import { SessionExpiredNotifier } from '@r-client/shared/feature/global-auth';
import {
  TEuropeJurisdictions,
  TRegionValidIds,
  useRegionContext,
  withRegionContext,
} from '@r-client/shared/feature/region';
import { setCookie, useAppConfig } from '@r-client/shared/util/core';

import { regionalSwitcherVariants } from '../../utils/regional-switcher';

export type TFooterConfig = IFooterProps & {
  hideFooter?: boolean;
};

export type THeaderConfig = IFooterProps & {
  hideRaiseCapitalCta?: boolean;
  hideEventDisplay?: boolean;
  hideBanner?: boolean;
  hideTradeRepublicDisclosure?: boolean;
  bringToTop?: boolean;
};

export interface IMainLayoutProps {
  children?: ReactNode;
  sticky?: boolean;
  theme?: HEADER_THEMES;
  /**
   * Where the branded auth pages live for this request's host, forwarded to `LevelupMainHeader`
   * so its "Log in" link drops the `/levelup` segment on LevelUp's own hostname. Ignored by the
   * Republic header.
   */
  authPathPrefix?: string;
  overlay?: boolean;
  transparent?: boolean;
  transparentGray?: boolean;
  footerConfig?: TFooterConfig;
  headerConfig?: THeaderConfig;
  layoutConfig?: TLayoutConfig;
}

const globalRoutes = ['/', '/hldmzfknye'];

export const MainLayout = withRegionContext(
  observer<IMainLayoutProps>(function MainLayoutComponent({
    children,
    sticky,
    theme,
    authPathPrefix,
    overlay = false,
    footerConfig,
    headerConfig,
    layoutConfig,
  }) {
    const { route } = useRouter();
    const { region: currentRegion, setRegion } = useRegionContext();
    const { republicEuropeBaseUrl, publicTld } = useAppConfig();

    const { viewer, isLoading: authLoading } = useAuth();

    const analytics = useAnalytics();

    const isLoading = authLoading;

    const onRegionSwitch = useMemo(
      () => (id: TRegionValidIds) => {
        setCookie({
          name: 'region',
          value: id,
          options: {
            maxAge: 400 * 24 * 60 * 60 * 1000,
            sameSite: 'lax',
            domain: publicTld,
          },
        });
        setRegion(id);
      },
      [setRegion, publicTld]
    );

    const onRegionSwitchForEuropeJurisdictions = useMemo(
      () => (id: TEuropeJurisdictions) => {
        setCookie({
          name: 'jurisdiction',
          value: id,
          options: {
            maxAge: 400 * 24 * 60 * 60,
            sameSite: 'lax',
            domain: publicTld,
          },
        });
      },
      [publicTld]
    );

    const regionSwitcherSettings: IRegionSwitcherSettings | undefined =
      useMemo(() => {
        if (isLoading) {
          return undefined;
        }
        return {
          variants: regionalSwitcherVariants({
            onChange: onRegionSwitch,
            onChangeForEuropeJurisdictions:
              onRegionSwitchForEuropeJurisdictions,
            mode: globalRoutes.includes(route) ? 'cookie-only' : 'navigate',
            republicEuropeBaseUrl:
              republicEuropeBaseUrl ?? 'https://europe.republic.com',
          }),
          activeVariantID: currentRegion,
        };
      }, [
        isLoading,
        onRegionSwitch,
        onRegionSwitchForEuropeJurisdictions,
        route,
        republicEuropeBaseUrl,
        currentRegion,
      ]);

    const onScrollTopClick = () =>
      analytics.track({ name: 'Back to top link - clicked' });

    const footer = !footerConfig?.hideFooter ? (
      <Footer {...footerConfig} />
    ) : null;

    return (
      <>
        <SessionExpiredNotifier />
        <AdminMenu />
        {headerConfig?.hideTradeRepublicDisclosure === false && <Disclosure />}
        {!headerConfig?.hideBanner ? <BannerWrapper /> : null}
        <Layout
          config={layoutConfig}
          header={
            // LevelUp gets its own header component, not a theme of Republic's: the two share
            // only an outer box, and expressing the difference as conditions inside the shared
            // header left the mobile one rendering Republic navigation under a black bar.
            theme === 'levelup' ? (
              <LevelupMainHeader
                overlay={overlay}
                sticky={sticky}
                user={{ viewer, isLoading }}
                authPathPrefix={authPathPrefix}
              />
            ) : (
              <MainHeader
                theme={theme}
                overlay={overlay}
                sticky={sticky}
                user={{ viewer, isLoading }}
                region={currentRegion === 'global' ? 'global' : 'us'}
                regionSwitcherSettings={regionSwitcherSettings}
              />
            )
          }
          footer={footer}
        >
          {children}
        </Layout>
        <div onClick={onScrollTopClick}>
          <ScrollTopButton />
        </div>
      </>
    );
  })
);
